import React, { useState } from 'react'
import cn, { Argument as ClassName } from 'classnames'
import styled from 'styled-components'

import BootstrapModalLayout from '../../ui/BootstrapModalLayout'
import { Button } from '../button'
import Text from '../../ui/Text'
import { IFilters } from '../../support/components/charge-point-logs-widget'
import useText from '../../common/hooks/useText'

const SContainer = styled.div`
    margin: -15px;
`

const SFilterModalHeader = styled.header`
    padding: 15px;
    border-bottom: 1px solid #e5e5e5;
    font-weight: 800;
    font-size: 18px;
`

const SFilterModalBody = styled.main`
    padding: 15px;
    border-bottom: 1px solid #e5e5e5;
`

const SFilterModalFooter = styled.footer`
    padding: 15px;
    display: flex;
    justify-content: space-between;
    flex-direction: row-reverse;
`

const FilterCheckboxWrapper = styled.label`
    display: flex;
    align-items: center;
    padding-right: 8px;
    font-weight: normal;
    cursor: pointer;
`

const SFiltersWrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
`

const SFiltersGroupTitle = styled.div`
    margin: 15px 0 10px 0px;
    font-weight: 700;
`

export type FilterModalProps = {
    className?: ClassName
    style?: React.CSSProperties
    onClose: (newFilters: IFilters) => void
    filters: IFilters
}

const FilterTitle = ({ filter }: { filter: string }): React.ReactElement => {
    switch (filter) {
        case 'logLevel':
            return <Text code="lblLogLevel" />
        case 'debug':
            return <Text code="lblDebugField" />
        case 'info':
            return <Text code="lblInfoField" />
        case 'warn':
            return <Text code="lblWarningField" />
        case 'error':
            return <Text code="lblErrorField" />
        case 'origin':
            return <Text code="lblOrigin" />
        case 'lb':
            return <Text code="lblLoadbalancerShortField" />
        case 'ocpp':
            return <Text code="lblOcppField" />
        case 'capi':
            return <Text code="lblCapiField" />
        case 'measure':
            return <Text code="lblMeasureField" />
        case 'batch':
            return <Text code="lblBatchField" />
        case 'device':
            return <Text code="lblDeviceField" />
        case 'web':
            return <Text code="lblWebField" />

        case 'WifiInfo':
            return <Text code="lblWifiInfo" />
        case 'Temperature':
            return <Text code="lblTemperature" />
        case 'ProcessorVoltage':
            return <Text code="lblProcessorVoltage" />
        case 'PwmStateShifts':
            return <Text code="lblPWMStateShifts" />
        case 'Pwm':
            return <Text code="lblPWM" />
        case 'GfcidcWaveform':
            return <Text code="lblGFCIDCWaveForm" />
        case 'ClientInfo':
            return <Text code="lblClientInfo" />
        case 'dataType':
            return <Text code="lblDataType" />
        default:
            return <span>{filter}</span>
    }
}

export function FilterModal({ className, style, onClose, filters }: FilterModalProps): React.ReactElement {
    const text = useText()
    const [selectedFilters, setSelectedFilters] = useState(() => ({ ...filters }))

    return (
        <BootstrapModalLayout size={300}>
            <SContainer className={cn(className, 'FilterModal')} style={style}>
                <SFilterModalHeader data-testid="filter-modal">
                    <Text code="lblFilterModal" />
                </SFilterModalHeader>
                <SFilterModalBody>
                    {Object.keys(selectedFilters).map((filterGroup, i) => {
                        const filterGroupValue = selectedFilters[filterGroup]
                        return (
                            <div key={Math.random()}>
                                <SFiltersGroupTitle data-testid={'filter-group-' + i}>
                                    <FilterTitle filter={filterGroup} />
                                </SFiltersGroupTitle>
                                <SFiltersWrapper>
                                    {typeof filterGroupValue === 'object' ? (
                                        Object.keys(filterGroupValue).map((filter, j) => (
                                            <FilterCheckboxWrapper key={Math.random()} data-testid={'filter-name' + j}>
                                                <input
                                                    className="checkbox-black"
                                                    type="checkbox"
                                                    style={{ margin: '0 2px 3px 0' }}
                                                    checked={filterGroupValue[filter]}
                                                    onChange={(event) => {
                                                        setSelectedFilters({
                                                            ...selectedFilters,
                                                            [filterGroup]: {
                                                                ...filterGroupValue,
                                                                [filter]: event.target.checked,
                                                            },
                                                        })
                                                    }}
                                                    data-testid={'filter-' + j}
                                                />
                                                <FilterTitle filter={filter} />
                                            </FilterCheckboxWrapper>
                                        ))
                                    ) : (
                                        <FilterCheckboxWrapper data-testid={'filter-name-one-' + i}>
                                            <input
                                                className="checkbox-black"
                                                type="checkbox"
                                                style={{ margin: '0 2px 3px 0' }}
                                                checked={filterGroupValue}
                                                onChange={(event) => {
                                                    setSelectedFilters({
                                                        ...selectedFilters,
                                                        [filterGroup]: event.target.checked,
                                                    })
                                                }}
                                                data-testid={'filter-one-' + i}
                                            />
                                            <FilterTitle filter={filterGroup} />
                                        </FilterCheckboxWrapper>
                                    )}
                                </SFiltersWrapper>
                            </div>
                        )
                    })}
                </SFilterModalBody>
                <SFilterModalFooter>
                    <Button title={text('btnClose')} onClick={() => onClose(selectedFilters)} />
                </SFilterModalFooter>
            </SContainer>
        </BootstrapModalLayout>
    )
}
